import React from 'react';
import * as icons from '@ant-design/icons';
import type { MenuDataItem } from '@ant-design/pro-components';
import type { MenuTreeItemVo } from '@/api/system/menu/type';

/**
 * 将菜单 path 转为国际化 key,如 /ops/version → menu.ops.version
 * ProLayout 在 layout.locale=true 时,会用 item.locale 作为 key 调 formatMessage 翻译,
 * 找不到则回退到 item.name(后端原名),故不会因 key 缺失而出错。
 */
const pathToMenuLocaleKey = (path?: string): string => {
  if (!path || path === '/') return '';
  const segments = path.replace(/^\//, '').replace(/\/$/, '').replace(/\//g, '.');
  return segments ? `menu.${segments}` : '';
};

/**
 * 将图标名称字符串转换为 React 组件
 * @param iconName 图标名称，如 "SettingOutlined"
 */
export const getIconComponent = (iconName?: string): React.ReactNode => {
  // 过滤无效图标值（#、空字符串、null、undefined）
  if (!iconName || iconName === '#' || iconName.trim() === '') return undefined;

  // 尝试从 @ant-design/icons 获取图标组件
  const IconComponent = (icons as any)[iconName];
  if (IconComponent) {
    return React.createElement(IconComponent);
  }

  // 如果找不到，返回默认图标
  return undefined;
};

/**
 * 将后端菜单数据转换为 ProLayout 需要的格式
 * @param menuList 后端返回的菜单列表
 * @returns ProLayout 格式的菜单数据
 */
export const convertToMenuData = (menuList?: MenuTreeItemVo[]): MenuDataItem[] => {
  if (!menuList || menuList.length === 0) return [];

  return menuList
    .filter((menu) => {
      // 过滤掉按钮类型（F）
      if (menu.menuType === 'F') return false;
      // 过滤掉停用的菜单（status='1'）
      if (menu.status === '1') return false;
      return true;
    })
    .map((menu) => {
      const menuItem: MenuDataItem = {
        key: String(menu.id),
        name: menu.menuName || '',
        path: menu.path || '',
        locale: pathToMenuLocaleKey(menu.path) || undefined,
        icon: getIconComponent(menu.icon),
        hideInMenu: menu.visible === '1',
        order: menu.orderNum,
      };

      // 递归处理子菜单
      if (menu.children && menu.children.length > 0) {
        menuItem.children = convertToMenuData(menu.children);
      }

      return menuItem;
    });
};

/**
 * 从菜单数据中提取所有权限标识
 * @param menuList 后端返回的菜单列表
 * @returns 权限标识列表
 */
export const extractPermissions = (menuList?: MenuTreeItemVo[]): string[] => {
  if (!menuList || menuList.length === 0) return [];

  const permissions: string[] = [];

  const traverse = (items: MenuTreeItemVo[]) => {
    items.forEach((item) => {
      if (item.perms) {
        permissions.push(item.perms);
      }
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    });
  };

  traverse(menuList);
  return permissions;
};
